
































































import { InteractionApi } from '@/api/interaction';
import editInteractionDialog from '@/components/client/view-client/edit-interaction-dialog.vue';
import viewClientTaskDialog from '@/components/client/view-client/view-client-task-dialog.vue';
import viewEmailDialog from '@/components/client/view-client/view-email-dialog.vue';
import viewInteractionDialog from '@/components/client/view-client/view-interaction-dialog.vue';
import viewMessageDialog from '@/components/client/view-client/view-message-dialog.vue';
import { ClientAccount } from '@/shared/clients/client-account';
import { ClientContactHistory } from '@/shared/clients/client-account-contact-history';
import { ClientAccountEmailHistory } from '@/shared/clients/client-account-email-history';
import { ClientAccountMessageHistory } from '@/shared/clients/client-account-message-history';
import { ClientAccountTaskHistory } from '@/shared/clients/client-account-task-history';
import { ContactHistoryItem } from '@/shared/contact-history-item';
import { ContactItemType } from '@/shared/contact-item-type';
import { Interaction } from '@/shared/interaction';
import { MessageType } from '@/shared/message-type';
import { InteractionOutcomeType } from '@/shared/types/interaction-outcome-type';
import { InteractionType } from '@/shared/types/interaction-type';
import { User } from '@/shared/user';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const interactionApi = new InteractionApi();
const typesModule = namespace('types');
const userModule = namespace('user');
const usersModule = namespace('users');

@Component({
    components: {
        viewClientTaskDialog,
        viewEmailDialog,
        viewInteractionDialog,
        viewMessageDialog,
        editInteractionDialog,
    },
})
export default class ContactHistory extends Vue {
    @typesModule.Getter('interactionTypes') private interactionTypes!: Array<InteractionType>;
    @typesModule.Getter('interactionOutcomeTypes') private interactionOutcomeTypes!: Array<InteractionOutcomeType>;

    @userModule.Getter('isAdministrator') private isAdministrator!: boolean;

    @usersModule.Getter('users') private users!: Array<User>;

    @Prop({ required: true }) private client!: ClientAccount;
    @Prop({ required: true }) private contactHistory!: ClientContactHistory;

    private isSaving: boolean = false;
    private viewTaskDialogVisible: boolean = false;
    private viewEmailDialogVisible: boolean = false;
    private viewInteractionDialogVisible: boolean = false;
    private viewMessageDialogVisible: boolean = false;
    private editInteractionDialogVisible: boolean = false;
    private deleteInteractionConfirmationDialogVisible: boolean = false;
    private selectedTask: ClientAccountTaskHistory | null = null;
    private selectedEmail: ClientAccountEmailHistory | null = null;
    private selectedInteraction: Interaction | null = null;
    private selectedMessage: ClientAccountMessageHistory | null = null;

    private get contactHistoryItems(): Array<ContactHistoryItem> {
        const history = new Array<ContactHistoryItem>();

        if (this.contactHistory.interactionHistory != null && this.contactHistory.interactionHistory.length) {
            for (const interaction of this.contactHistory.interactionHistory) {
                const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === interaction.interactionOutcomeTypeId);
                history.push({
                    id: interaction.id!,
                    title: this.interactionTypeDescription(interaction.interactionTypeId!),
                    outcome: this.interactionOutcomeDescription(interaction.interactionOutcomeTypeId!),
                    appointmentDate: interactionOutcomeType != null && (interactionOutcomeType.isAppointmentBooked || interactionOutcomeType.isAppointmentRebooked) ? interaction.appointmentDate : null,
                    createdDate: interaction.createdDate!,
                    createdBy: interaction.createdBy!,
                    lastModifiedDate: interaction.lastModifiedDate,
                    lastModifiedBy: interaction.lastModifiedBy,
                    type: ContactItemType.Interaction,
                });
            }
        }

        if (this.contactHistory.emailHistory != null && this.contactHistory.emailHistory.length) {
            for (const email of this.contactHistory.emailHistory) {
                history.push({
                    id: email.id,
                    title: 'Email Sent',
                    outcome: email.type,
                    createdDate: email.sentDate,
                    createdBy: null,
                    type: ContactItemType.Email,
                });
            }
        }

        if (this.contactHistory.messageHistory != null && this.contactHistory.messageHistory.length) {
            for (const message of this.contactHistory.messageHistory) {
                history.push({
                    id: message.id,
                    title: 'SMS Sent',
                    outcome: this.messageTypeDescription(message),
                    createdDate: message.sentDate,
                    createdBy: null,
                    type: ContactItemType.Message,
                });
            }
        }

        if (this.contactHistory.taskHistory != null && this.contactHistory.taskHistory.length) {
            for (const task of this.contactHistory.taskHistory) {
                history.push({
                    id: task.id!,
                    title: 'Confirmation Letter Sent',
                    outcome: task.actioned! ? 'Actioned' : 'Not Actioned',
                    createdDate: task.completedDate!,
                    createdBy: task.completedBy!,
                    type: ContactItemType.Task,
                });
            }
        }

        return history.sort((n1, n2) => {
            if (n1.createdDate! < n2.createdDate!) { return 1; }
            if (n1.createdDate! > n2.createdDate!) { return -1; }
            return 0;
        });
    }

    private interactionTypeDescription(interactionTypeId: string | null): string {
        if (interactionTypeId == null) {
            return '';
        }

        const interactionType = this.interactionTypes.find((type) => type.id === interactionTypeId);
        return interactionType == null ? '' : interactionType.value;
    }

    private interactionOutcomeDescription(interactionOutcomeTypeId: string | null): string {
        if (interactionOutcomeTypeId == null) {
            return '';
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === interactionOutcomeTypeId);
        return interactionOutcomeType == null ? '' : interactionOutcomeType.value;
    }

    private messageTypeDescription(message: ClientAccountMessageHistory): string {
        if (message == null) {
            return '';
        }

        switch (message.type!) {
            case MessageType.AppointmentConfirmation:
                return 'Appointment Confirmation';
            case MessageType.AppointmentReminder:
                return 'Appointment Reminder';
            case MessageType.AppointmentLookingForwardToSeeingYou:
                return 'Appointment LFTSY';
            default:
                return '';
        }
    }

    private userName(userId: string | null): string {
        if (userId == null) {
            return 'Unknown';
        }

        const user = this.users.find((u) => u.id === userId);
        return user == null ? 'Unknown' : `${user.firstName == null ? '' : user.firstName} ${user.lastName == null ? '' : user.lastName}`;
    }

    private async showViewDialogAsync(item: ContactHistoryItem): Promise<void> {
        this.selectedEmail = null;
        this.selectedInteraction = null;
        this.selectedMessage = null;
        this.selectedTask = null;

        switch (item.type) {
            case ContactItemType.Email:
                const email = this.contactHistory.emailHistory.find((e) => e.id === item.id);

                if (email == null) {
                    return;
                }

                this.selectedEmail = email;
                this.viewEmailDialogVisible = true;
                break;

            case ContactItemType.Interaction:
                const interaction = await interactionApi.getInteractionByIdAsync(item.id!);

                if (interaction == null) {
                    return;
                }

                this.selectedInteraction = { ...interaction };
                this.viewInteractionDialogVisible = true;
                break;

            case ContactItemType.Message:
                const message = this.contactHistory.messageHistory.find((m) => m.id === item.id);

                if (message == null) {
                    return;
                }

                this.selectedMessage = message;
                this.viewMessageDialogVisible = true;
                break;

            case ContactItemType.Task:
                const task = this.contactHistory.taskHistory.find((t) => t.id === item.id);

                if (task == null) {
                    return;
                }

                this.selectedTask = task;
                this.viewTaskDialogVisible = true;
                break;
        }
    }

    private async showEditDialogAsync(item: ContactHistoryItem): Promise<void> {
        if (item.type !== ContactItemType.Interaction) {
            return;
        }

        const interaction = await interactionApi.getInteractionByIdAsync(item.id!);

        if (interaction == null) {
            return;
        }

        this.selectedInteraction = { ...interaction };
        this.editInteractionDialogVisible = true;
    }

    private async showDeleteConfirmationAsync(item: ContactHistoryItem): Promise<void> {
        if (item.type !== ContactItemType.Interaction) {
            return;
        }

        const interaction = await interactionApi.getInteractionByIdAsync(item.id!);

        if (interaction == null) {
            return;
        }

        this.selectedInteraction = { ...interaction };
        this.deleteInteractionConfirmationDialogVisible = true;
    }

    private onInteractionUpdated(): void {
        this.$emit('interaction-updated');
        this.onCancel();
    }

    private async onDeleteAsync(): Promise<void> {
        this.isSaving = true;

        try {
            await interactionApi.deleteInteractionAsync(this.selectedInteraction!.id!);
            this.$emit('interaction-deleted');
        } catch (e) {
            if (e instanceof Error) {
                this.$snackbar.open({ message: e.message, type: 'is-danger' });
            } else {
                this.$snackbar.open({ message: 'Could not delete interaction, please try again.', type: 'is-danger' });
            }
        }

        this.selectedInteraction = null;
        this.isSaving = false;
        this.onCancel();
    }

    private onCancel(): void {
        this.viewEmailDialogVisible = false;
        this.viewInteractionDialogVisible = false;
        this.viewMessageDialogVisible = false;
        this.viewTaskDialogVisible = false;
        this.editInteractionDialogVisible = false;
        this.deleteInteractionConfirmationDialogVisible = false;

        this.selectedTask = null;
        this.selectedEmail = null;
        this.selectedInteraction = null;
        this.selectedMessage = null;
    }
}
